import {createSelector} from 'reselect'

import type {State} from '../../../../../reducers/types'
import {stringifyId} from '../../../../../types'
import {getAgentPoolsTree, getFavoriteAgentTree} from '../../AgentsPages.selectors'
import type {
  AgentsTreeAgentPoolNode,
  AgentsTreeItemPool,
  AgentsTreeItemTitle,
} from '../../AgentsSidebar/AgentsSidebar.types'
import {
  AgentTreeItemCategory,
  AgentTreeItemType,
  AgentTreeTitleType,
} from '../../AgentsSidebar/AgentsSidebar.types'

const favoriteTitle: AgentsTreeItemTitle = {
  id: AgentTreeTitleType.FAVORITE,
  type: AgentTreeItemType.TITLE,
  editable: false,
  key: 'agent-pools-favorite-title',
  title: 'FAVORITES',
  level: 0,
}
const allTitle: AgentsTreeItemTitle = {
  id: AgentTreeTitleType.POOLS,
  type: AgentTreeItemType.TITLE,
  editable: false,
  key: 'agent-pools-title',
  title: 'EVERYTHING',
  height: 38,
  level: 0,
}

const matchTitle = (title: string | undefined, searchQuery: string) =>
  title?.toLowerCase().includes(searchQuery.trim().toLowerCase())

function matchAgentsTree(
  agentsTree: AgentsTreeAgentPoolNode[],
  searchQuery: string,
): AgentsTreeAgentPoolNode[] {
  if (searchQuery === '') {
    return agentsTree
  }

  const matchingAgentsTree: Array<AgentsTreeAgentPoolNode> = []

  for (const node of agentsTree) {
    const poolNode = {...node, matching: matchTitle(node.title, searchQuery)}

    if (poolNode.matching) {
      matchingAgentsTree.push(poolNode)
    }
  }

  return matchingAgentsTree
}

const getMatchingAgentsTree = createSelector(
  [getAgentPoolsTree, (_: State, props: {searchQuery: string}) => props.searchQuery],
  matchAgentsTree,
)

const getMatchingFavoriteAgentsTree = createSelector(
  [getFavoriteAgentTree, (_: State, props: {searchQuery: string}) => props.searchQuery],
  matchAgentsTree,
)

export const getAgentPoolsFavoriteDialogItems = createSelector(
  [getMatchingAgentsTree, getMatchingFavoriteAgentsTree],
  (agentsTree, favoriteAgentsTree) => {
    const agentPool = (
      node: AgentsTreeAgentPoolNode,
      category: AgentTreeItemCategory,
    ): AgentsTreeItemPool => {
      const {id, title, count, matching} = node

      return {
        id,
        category,
        key: `${category}-agent-pool-${stringifyId(id)}`,
        type: AgentTreeItemType.POOL,
        title,
        level: 0,
        count,
        busyCount: 0,
        disconnectedCount: 0,
        matching,
        expanded: false,
        active: false,
        containsActive: false,
        focusable: true,
        expandable: false,
      }
    }

    const items: (AgentsTreeItemPool | AgentsTreeItemTitle)[] = []

    if (favoriteAgentsTree.length > 0) {
      items.push(favoriteTitle)
    }

    items.push(...favoriteAgentsTree.map(node => agentPool(node, AgentTreeItemCategory.FAVORITE)))

    if (agentsTree.length > 0) {
      items.push(allTitle)
    }

    items.push(...agentsTree.map(node => agentPool(node, AgentTreeItemCategory.MAIN)))

    return items
  },
)
